import { For, Show, useContext } from 'solid-js';
import { Text } from '../../../../ui-components/text/text';
import { Button } from '../../../../ui-components/button/button';
import { Heading } from '../../../../ui-components/heading/heading';
import { EntitySlug, FormattedPostItem, pdfStore, setPdfStore } from '../../pdf-store';
import { StyledFlexRow } from '../../../../ui-components/utility-style-components/flex';
import { StyledVerticalSpace } from '../../../../ui-components/utility-style-components/spacing';
import { StyledPostItem, StyledPostNotFoundExplainer, StyledRemoveButton, StyledTextContainer } from './arrangement.style';
import { AppContext } from '../../../../app-context-provider/app-context-provider';
import { LoadingSpinner } from '../../../loading-spinner/loading-spinner';
import { InfoIcon } from '../../../icons-library/info';
import theme from '../../../../style/theme';

export const Arrangement = (props: { 
    saveExistingCollection: () => void
    reset: () => void
}) => {

    const { localize, viewport } = useContext(AppContext);

    const smallerButtons = () => viewport.width <= 750;

    const dragStart = (e: DragEvent) => {
        const target = e.target as HTMLElement;
        const id = target?.dataset.id;
        if (!id) return;
        e.dataTransfer?.setData('text/plain', id);
    };

    const dragOver = (e: DragEvent) => {
        e.preventDefault();
        const target = e.target as HTMLElement;
        const nearestElement: HTMLElement | null = target.closest('[data-id]');
        if (nearestElement) {
            nearestElement.classList.add('drag-over'); // Highlight the element being dragged over
        }
    };

    const dragLeave = (e: DragEvent) => {
        const target = e.target as HTMLElement;
        const nearestElement: HTMLElement | null = target.closest('[data-id]');
        if (nearestElement) {
            nearestElement.classList.remove('drag-over'); // Remove highlight when leaving
        }
    };

    const drop = (e: DragEvent) => {
        e.preventDefault();
    
        const draggedItemId = e.dataTransfer?.getData('text/plain');
        const target = e.target as HTMLElement;
        const nearestElement: HTMLElement | null = target.closest('[data-id]');
    
        if (!nearestElement) return;
    
        nearestElement.classList.remove('drag-over'); // Remove highlight after drop
    
        const droppedOntoItemId = nearestElement.dataset.id;
    
        const postsCopy = [ ...pdfStore.workingCollectionPosts ];
    
        const oldIndex = postsCopy.findIndex((item) => item.objectID === draggedItemId);
        const item = postsCopy[oldIndex];
        const newIndex = postsCopy.findIndex((item) => item.objectID === droppedOntoItemId);
    
        postsCopy.splice(newIndex, 0, item);
    
        // if moving up the list we need to remove the item from the old index
        if (newIndex < oldIndex) {
            postsCopy.splice(oldIndex + 1, 1);
        } else {
            postsCopy.splice(oldIndex, 1);
        }
    
        setPdfStore('workingCollectionPosts', postsCopy);
    };

    const removeItem = (id: string) => {
        const collection = [ ...pdfStore.workingCollectionPosts ];
        const index = collection.findIndex((item) => item.objectID === id);
        collection.splice(index, 1);
        setPdfStore('workingCollectionPosts', collection);
    };

    const ActionButtons = () => (
        <div>
            <StyledFlexRow justifyContent='end'>
                <Show when={pdfStore.workingCollectionPosts.length}>
                    <Button
                        label={localize('clear-all', 'Clear All')}
                        variant='tertiary'
                        onClick={() => props.reset()}
                        customCss='margin-right: 1.5rem;'
                        disabled={isMutating()}
                        small={smallerButtons()}
                    />

                    <Show when={pdfStore.status === 'editing' && pdfStore.activeCollectionId}>
                        <Button
                            label={localize('pdf-builder-update-existing-button', 'Update')}
                            variant='secondary'
                            onClick={() => {
                                props.saveExistingCollection();
                            }}
                            customCss='margin-right: 1.5rem;'
                            disabled={isMutating()}
                            small={smallerButtons()}
                        />
                    </Show>

                    <Button
                        label={localize('pdf-builder-create', 'Create')}
                        onClick={() => setPdfStore('status', 'naming')}
                        variant={'primary'}
                        disabled={isMutating()}
                        small={smallerButtons()}
                    />
                </Show>
            </StyledFlexRow>
            <StyledVerticalSpace size={1} />
        </div>

    );
    
    const isMutating = () => pdfStore.status === 'creating' || pdfStore.status === 'updating-existing';

    const CreatingStatus = () => (
        <Show when={pdfStore.status === 'creating'}>
            <div>
                <StyledFlexRow alignItems='center' gap='1rem'>
                    <Text fontSize='small' fontStyle='italic' noBlockSpacing>
                        {localize('pdf-creating-collection-status', 'Creating collection...')}
                    </Text>
                    <LoadingSpinner size='2.2rem' />
                </StyledFlexRow>
                <StyledVerticalSpace size={2} />
            </div>
        </Show>
    );

    const VerifyingPostsStatus = () => (
        <Show when={!pdfStore.workingCollectionPosts.length && pdfStore.status === 'editing'}>
            <StyledFlexRow alignItems='center' gap='1rem'>
                <Text fontSize='small' fontStyle='italic' noBlockSpacing>
                    {localize('pdf-verifying-status', 'Verifying posts...')}
                </Text>
                <LoadingSpinner size='2.2rem' />
            </StyledFlexRow>
        </Show>
    );

    const PostNotFoundExplainer = () => (
        <StyledPostNotFoundExplainer>
            <StyledFlexRow gap='0.75rem'>
                <InfoIcon
                    width={2.2}
                    height={2.2}
                    stroke={theme.palette.darkGray}
                />

                <Text fontSize='small' fontStyle='italic' color='gray' noBlockSpacing>
                    {localize('pdf-post-unavailable', 'This post is not available and will be removed upon saving/creating the collection.')}
                </Text>
            </StyledFlexRow>
        </StyledPostNotFoundExplainer>
    );

    const ArrangementItem = ({ post }: { post: FormattedPostItem; }) => (
        <StyledPostItem
            draggable={!isMutating()}
            data-id={post.objectID}
            disabled={isMutating() || !post.available}
        >
            <StyledFlexRow justifyContent='space-between' alignItems='end'>
                <StyledTextContainer>
                    <Heading tag='h3' variant='small' noBlockSpacing={true} simpleColor={isMutating() ? theme.palette.gray : undefined}>
                        {pdfStore.entityNameMap[ post?.type as EntitySlug ]}
                    </Heading>

                    <Text
                        fontSize='normal'
                        color={isMutating() ? 'gray' : undefined}
                        noBlockSpacing={true}
                    >
                        {post.postTitle}
                    </Text>

                </StyledTextContainer>
                <StyledRemoveButton>
                    <Button
                        label={localize('remove', 'Remove')}
                        variant='tertiary'
                        onClick={() => removeItem(post.objectID)}
                        disabled={isMutating()}
                        small={smallerButtons()}
                    />
                </StyledRemoveButton>
            </StyledFlexRow>

            <Show when={!post.available}>
                <PostNotFoundExplainer />
            </Show>
        </StyledPostItem>
    );

    return (
        <>
            <StyledVerticalSpace size={5} />

            <StyledFlexRow justifyContent='space-between'>
                <Heading tag="h4" variant='small'>
                    {localize('pdf-arrangement', 'Arrangement')}
                </Heading>

                <Show when={ pdfStore.workingCollectionPosts.length > 1 } fallback={<StyledVerticalSpace size={ 3 } />}>
                    <Text fontSize='small' noBlockSpacing={ true } fontStyle='italic' color='gray'>
                        {localize('pdf-drag-and-drop-to-reorder', 'Drag and drop to reorder')}
                    </Text>
                </Show>
            </StyledFlexRow>

            <Show when={!pdfStore.workingCollectionPosts.length && pdfStore.status !== 'editing'}>
                <Text fontSize='small' fontStyle='italic'>
                    {localize('pdf-arrangement-explainer', 'Add posts to your PDF by searching for them and clicking on them')}
                </Text>
            </Show>

            <VerifyingPostsStatus/>
                        
            <CreatingStatus/>

            <Show when={ pdfStore.workingCollectionPosts.length }>
                <div
                    onDragStart={ (e) => dragStart(e) }
                    onDragOver={ (e) => dragOver(e) }
                    onDragLeave={ (e) => dragLeave(e) } // Add dragLeave handler
                    onDrop={ (e) => drop(e) }
                >
                    <For each={ pdfStore.workingCollectionPosts }>{(post) => (
                        <ArrangementItem post={ post } />
                    )}</For>
                </div>
            </Show>

            <ActionButtons/>
        </>
    );
};